import { useState } from "react";
import { Affix, Button, Empty, EnvBadge, ErrorNotice, Field, Input, Loading, Modal, Panel, Select, useToast } from "../../components/ui";
import { gameApi, type GameApi } from "../../lib/api";
import { formatMoney, parseMoney, type CurrencyLike } from "../../lib/money";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { Link } from "../../lib/router";
import { useClient } from "../../lib/session";
import type { Bank, CardProgram, CardProgramInput, Env } from "../../lib/types";
import type { GameTabProps } from "./Game";

/** "7 game days", "2 game hours", "90 game minutes". */
export function gameDuration(seconds: number): string {
  const units: [number, string][] = [[86400, "day"], [3600, "hour"], [60, "minute"]];
  for (const [n, name] of units) {
    if (seconds >= n && seconds % n === 0) {
      const v = seconds / n;
      return `${v} game ${name}${v === 1 ? "" : "s"}`;
    }
  }
  return `${seconds} game seconds`;
}

export function GameCards({ game, env }: GameTabProps) {
  const api = gameApi(useClient(), { gameId: game.id, env });
  const banks = useQuery(`dev:banks:${game.id}:${env}`, () => api.banks());
  const [selected, setSelected] = useState<string | null>(null);
  const list = banks.data?.data ?? [];
  const bank = list.find((b) => b.id === selected) ?? list[0];

  if (banks.loading && !banks.data) return <Loading />;
  if (banks.error) return <ErrorNotice error={banks.error} onRetry={banks.reload} />;
  if (!bank) {
    return (
      <Empty title={`No ${env} banks yet`} action={<Link className="btn primary" to={`/dev/games/${game.id}/banks${env === "production" ? "?env=production" : ""}`}>Create a bank</Link>}>
        Cards belong to a bank. Create one first.
      </Empty>
    );
  }
  return (
    <div className="stack">
      {list.length > 1 && (
        <Field label="Bank">
          <Select value={bank.id} onChange={(e) => setSelected(e.target.value)} style={{ maxWidth: 360 }}>
            {list.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.bank_code})</option>)}
          </Select>
        </Field>
      )}
      <CardsSwitch api={api} gameId={game.id} env={env} bank={bank} />
      <Programs key={bank.id} api={api} gameId={game.id} env={env} bank={bank} />
    </div>
  );
}

function CardsSwitch({ api, gameId, env, bank }: { api: GameApi; gameId: string; env: Env; bank: Bank }) {
  const toast = useToast();
  const on = bank.feature_flags.cards === true;
  const flip = useAction(() => api.updateBank(bank.id, { feature_flags: { cards: !on } }));
  return (
    <Panel
      title={<>Debit cards in {bank.name} <EnvBadge env={env} /></>}
      sub="Players pay at merchants (NPC shops or player-run stores) with cards on their accounts. Payments are held when authorised and settle into the merchant's account on capture."
      actions={
        <Button variant={on ? undefined : "primary"} size="small" busy={flip.pending} onClick={async () => {
          if (await flip.run()) {
            invalidate(`dev:banks:${gameId}:${env}`);
            toast(on ? "Cards turned off: new cards and payments are refused" : "Cards turned on");
          }
        }}>
          {on ? "Turn cards off" : "Turn cards on"}
        </Button>
      }
    >
      <ErrorNotice error={flip.error} />
      <p className="small muted" style={{ margin: 0 }}>
        {on ? <><span className="badge ok">On</span> Cards can be issued and used.</> : <><span className="badge bad">Off</span> Turn cards on, then add a program below.</>}
        {" "}Card fees (issuance and per payment) are set in <Link to={`/dev/games/${gameId}/fees${env === "production" ? "?env=production" : ""}`}>Fees &amp; charges</Link>.
      </p>
    </Panel>
  );
}

function Programs({ api, gameId, env, bank }: { api: GameApi; gameId: string; env: Env; bank: Bank }) {
  const toast = useToast();
  const key = `dev:card-programs:${gameId}:${env}:${bank.id}`;
  const programs = useQuery(key, () => api.cardPrograms(bank.id));
  const [editing, setEditing] = useState<CardProgram | "new" | null>(null);
  const rows = programs.data?.data ?? [];
  const cur = bank.base_currency;
  const limit = (v: number | null) => (v == null ? "No limit" : formatMoney(v, cur));

  return (
    <Panel title="Card programs" sub="A program is a card product, such as Classic or Gold. It sets the card number prefix (BIN), how long cards last, how long a payment holds funds, and default limits."
      actions={<Button variant="primary" size="small" onClick={() => setEditing("new")}>Add program</Button>} flush>
      <ErrorNotice error={programs.error} onRetry={programs.reload} />
      {programs.loading && !programs.data && <Loading />}
      {programs.data && rows.length === 0 && (
        <Empty title="No card programs yet" action={<Button variant="primary" onClick={() => setEditing("new")}>Add program</Button>}>
          With one active program, cards are issued from it automatically.
        </Empty>
      )}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>Program</th><th>Cards last</th><th>Payments hold for</th><th className="num">Per payment</th><th className="num">Per game day</th><th>Status</th><th /></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td><div className="cell-title">{p.name}</div><div className="cell-sub mono">BIN {p.bin}</div></td>
                  <td>{p.validity_months} months</td>
                  <td>{gameDuration(p.auth_expiry_seconds)}</td>
                  <td className="num">{limit(p.per_txn_limit_minor)}</td>
                  <td className="num">{limit(p.daily_limit_minor)}</td>
                  <td><span className={`badge ${p.status === "active" ? "ok" : "bad"}`}>{p.status === "active" ? "Active" : "Inactive"}</span></td>
                  <td className="num"><Button size="small" onClick={() => setEditing(p)}>Edit</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <ProgramModal api={api} bank={bank} cur={cur} program={editing === "new" ? null : editing} onClose={() => setEditing(null)}
          onSaved={(p) => { setEditing(null); invalidate(key); toast(editing === "new" ? `Program ${p.name} added (BIN ${p.bin})` : "Program saved"); }} />
      )}
    </Panel>
  );
}

function ProgramModal({ api, bank, cur, program, onClose, onSaved }: {
  api: GameApi; bank: Bank; cur: CurrencyLike; program: CardProgram | null; onClose: () => void; onSaved: (p: CardProgram) => void;
}) {
  const major = (v: number | null | undefined) => (v == null ? "" : String(v / 10 ** cur.exponent));
  const [name, setName] = useState(program?.name ?? "");
  const [bin, setBin] = useState("");
  const [months, setMonths] = useState(String(program?.validity_months ?? 36));
  const [holdDays, setHoldDays] = useState(String((program?.auth_expiry_seconds ?? 604800) / 86400));
  const [perTxn, setPerTxn] = useState(major(program?.per_txn_limit_minor));
  const [daily, setDaily] = useState(major(program?.daily_limit_minor));
  const [active, setActive] = useState(program ? program.status === "active" : true);
  const save = useAction(() => {
    const body: CardProgramInput = { name: name.trim(), validity_months: Number(months), auth_expiry_seconds: Math.round(Number(holdDays) * 86400) };
    const clear: CardProgramInput["clear"] = [];
    const per = perTxn.trim() ? parseMoney(perTxn, cur.exponent) : null;
    const day = daily.trim() ? parseMoney(daily, cur.exponent) : null;
    if (per) body.per_txn_limit_minor = per; else if (program) clear.push("per_txn_limit_minor");
    if (day) body.daily_limit_minor = day; else if (program) clear.push("daily_limit_minor");
    if (clear.length) body.clear = clear;
    if (program) {
      body.status = active ? "active" : "inactive";
      return api.updateCardProgram(bank.id, program.id, body);
    }
    if (bin.trim()) body.bin = bin.trim();
    return api.createCardProgram(bank.id, body);
  });
  return (
    <Modal open title={program ? `Edit ${program.name}` : "Add a card program"} onClose={onClose}
      onSubmit={async (e) => { e.preventDefault(); const p = await save.run(); if (p) onSaved(p); }}
      footer={<><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="primary" busy={save.pending} disabled={!name.trim()}>{program ? "Save" : "Add program"}</Button></>}>
      <div className="form">
        <Field label="Name" tip="Your name for the program, e.g. Classic or Gold. Players don't see it.">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Classic" maxLength={60} required autoFocus />
        </Field>
        {!program && (
          <Field label="BIN" hint="Optional. Leave empty to have one chosen."
            tip={<>The bank identification number: the first 6 digits of every card number in this program. It can't change after the program is added.</>}>
            <Input value={bin} onChange={(e) => setBin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder={`5${bank.bank_code}12`} />
          </Field>
        )}
        <div className="grid-2">
          <Field label="Cards last" hint="In game months."
            tip="How long a new card is valid. A card issued in March with 36 months works until the end of March three game years later, then payments are declined as expired.">
            <Affix after="months"><Input type="number" min={1} max={120} value={months} onChange={(e) => setMonths(e.target.value)} required /></Affix>
          </Field>
          <Field label="Payments hold for" hint="In game days." tipAlign="end"
            tip="An approved card payment reserves the money on the player's account until the shop captures it (takes the money) or releases it (cancels). If it does neither within this time, the money is released automatically. Payments made with instant capture never wait.">
            <Affix after="game days"><Input type="number" min={0.05} max={90} step="any" value={holdDays} onChange={(e) => setHoldDays(e.target.value)} required /></Affix>
          </Field>
          <Field label="Limit per payment" hint="Empty for no limit."
            tip="The largest single card payment. A bigger one is declined with exceeds_transaction_limit. You can set a different limit on an individual card.">
            <Affix before={cur.symbol ?? cur.code}><Input inputMode="decimal" value={perTxn} onChange={(e) => setPerTxn(e.target.value)} placeholder="No limit" /></Affix>
          </Field>
          <Field label="Limit per game day" hint="Empty for no limit." tipAlign="end"
            tip="The most one card can spend in a game day, counting approved and captured payments. Going over is declined with exceeds_daily_limit.">
            <Affix before={cur.symbol ?? cur.code}><Input inputMode="decimal" value={daily} onChange={(e) => setDaily(e.target.value)} placeholder="No limit" /></Affix>
          </Field>
        </div>
        {program && (
          <label className="row" style={{ gap: 8 }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>Active <span className="cell-sub">An inactive program issues no new cards; existing cards keep working.</span></span>
          </label>
        )}
        <ErrorNotice error={save.error} />
      </div>
    </Modal>
  );
}
